import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Bell, Medal, Calendar, Newspaper, UserCheck, X, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

function NotificacionesPopup() {
  const [open, setOpen] = useState(false);
  const [notificaciones, setNotificaciones] = useState([]);
  const navigate = useNavigate();
  const hasChecked = useRef(false);

  useEffect(() => {
    // Solo verificar una vez por sesión
    if (hasChecked.current) return;
    hasChecked.current = true;
    
    // Verificar si ya mostramos el popup en esta sesión
    const lastShownIds = sessionStorage.getItem('notif_popup_shown_ids');
    
    const timer = setTimeout(() => {
      checkNotificaciones(lastShownIds);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, []);

  const checkNotificaciones = async (lastShownIds) => {
    try {
      const response = await axios.get(`${API}/notifications`);
      const sinLeer = response.data.filter(n => !n.leida);
      
      if (sinLeer.length > 0) {
        // Obtener IDs de notificaciones actuales sin leer
        const currentIds = sinLeer.map(n => n.id).sort().join(',');
        
        // Solo mostrar si hay notificaciones NUEVAS (diferentes a las que ya mostramos)
        if (lastShownIds !== currentIds) {
          setNotificaciones(sinLeer.slice(0, 5));
          setOpen(true);
          // Guardar los IDs actuales
          sessionStorage.setItem('notif_popup_shown_ids', currentIds);
        }
      }
    } catch (error) {
      console.error('Error al cargar notificaciones:', error);
    }
  };

  const marcarLeida = async (notifId) => {
    try {
      await axios.put(`${API}/notifications/${notifId}/read`);
      const nuevas = notificaciones.filter(n => n.id !== notifId);
      setNotificaciones(nuevas);
      // Actualizar sessionStorage
      sessionStorage.setItem('notif_popup_shown_ids', nuevas.map(n => n.id).sort().join(','));
      if (nuevas.length === 0) {
        setOpen(false);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const marcarTodasLeidas = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`);
      setNotificaciones([]);
      sessionStorage.setItem('notif_popup_shown_ids', '');
      setOpen(false);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const getIcono = (tipo) => {
    switch (tipo) {
      case 'galon':
        return <Medal className="w-6 h-6 text-yellow-400" />;
      case 'cumpleanos':
        return <span className="text-2xl">🎂</span>;
      case 'entrenamiento':
      case 'plaza_liberada':
      case 'plaza_abierta':
        return <Calendar className="w-6 h-6 text-blue-400" />;
      case 'noticia':
        return <Newspaper className="w-6 h-6 text-purple-400" />;
      case 'aprobacion':
        return <UserCheck className="w-6 h-6 text-green-400" />;
      case 'evento_klan':
        return <span className="text-2xl">🏆</span>;
      case 'receta':
        return <span className="text-2xl">🍽️</span>;
      default:
        return <Bell className="w-6 h-6 text-gold" />;
    }
  };

  const getColorBorde = (tipo) => {
    switch (tipo) {
      case 'galon':
        return 'border-l-yellow-400';
      case 'cumpleanos':
        return 'border-l-pink-400';
      case 'entrenamiento':
      case 'plaza_liberada':
      case 'plaza_abierta':
        return 'border-l-blue-400';
      case 'noticia':
        return 'border-l-purple-400';
      case 'aprobacion':
        return 'border-l-green-400';
      case 'evento_klan':
        return 'border-l-indigo-400';
      case 'receta':
        return 'border-l-green-400';
      default:
        return 'border-l-gold';
    }
  };

  const irANotificaciones = () => {
    setOpen(false);
    navigate('/notificaciones');
  };

  if (notificaciones.length === 0) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-gray-900 border-gray-700 max-w-md sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-gold flex items-center gap-2 text-xl">
            <Bell className="w-6 h-6 animate-bounce" />
            ¡Tienes {notificaciones.length} notificación{notificaciones.length > 1 ? 'es' : ''}!
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-3 max-h-[60vh] overflow-y-auto py-2">
          {notificaciones.map((notif) => (
            <div 
              key={notif.id}
              className={`bg-gray-800 rounded-lg p-4 border-l-4 ${getColorBorde(notif.tipo)} relative group animate-fadeIn`}
            >
              <button
                onClick={() => marcarLeida(notif.id)}
                className="absolute top-2 right-2 text-gray-500 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
                title="Marcar como leída"
              >
                <X className="w-4 h-4" />
              </button>
              
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-1">
                  {getIcono(notif.tipo)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm sm:text-base leading-relaxed">
                    {notif.mensaje}
                  </p>
                  <p className="text-gray-500 text-xs mt-2">
                    {format(new Date(notif.fecha), "d 'de' MMMM, HH:mm", { locale: es })}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-2 pt-2 border-t border-gray-700">
          <Button
            variant="outline"
            className="flex-1 border-gray-600 text-gray-300 hover:text-white"
            onClick={marcarTodasLeidas}
          >
            Marcar todas leídas
          </Button>
          <Button
            className="flex-1 btn-primary-mk"
            onClick={irANotificaciones}
          >
            Ver todas
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default NotificacionesPopup;
